/**
 * Custom hook for UWB tag tracking with real-time updates
 * 
 * Backend already handles:
 * - Outlier detection (geometric consistency, statistical filtering)
 * - Kalman filtering (smoothing with velocity prediction)
 * - Boundary checking
 * 
 * Frontend just displays the backend's calculated positions with minimal visual smoothing
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';

// Use the same API URL logic as api.js
// In development, connect to backend on same host but different port (WebSocket can't use HTTP proxy)
// In production, use the configured API URL or default to localhost
const getSocketURL = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (process.env.NODE_ENV === 'production') {
    return 'http://localhost:5001';
  }
  // Development: use same host as frontend but backend port
  const backendPort = process.env.REACT_APP_BACKEND_PORT || '5001';
  return `${window.location.protocol}//${window.location.hostname}:${backendPort}`;
};
const API_URL = getSocketURL();
const TOKEN_KEY = 'asset_track_token'; // Match the token key from api.js

let socket = null;

// Minimal visual smoothing config (just for animation smoothness, not accuracy)
const VISUAL_SMOOTHING = {
  ALPHA: 0.7, // Light smoothing (0.7 = 70% new position, 30% old) - just for visual smoothness
  MIN_CONFIDENCE: 0.1 // Only reject very low confidence readings
};

export function useUWBTracking(warehouseId = null, hubs = []) {
  const [uwbTags, setUwbTags] = useState([]);
  const [ghostTags, setGhostTags] = useState([]); // only populated in 'all' mode
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [positioningMethod, setPositioningMethodState] = useState('pairwise'); // 'pairwise' | 'linear' | 'pairwise2' | 'linear2' | 'all'
  
  // Store last displayed position per tag (for minimal visual smoothing only)
  const lastPositionsRef = useRef(new Map()); // tagId -> { x, y, timestamp }
  
  // Store latest hubs in a ref so socket handler can access them
  const hubsRef = useRef(hubs);
  useEffect(() => {
    hubsRef.current = hubs;
  }, [hubs]);
  
  // Track which tags we've already logged warnings for (to avoid spam)
  const loggedWarningsRef = useRef(new Set());
  // Track if we've already fetched readings for warnings (to avoid repeated API calls)
  const readingsFetchedRef = useRef(false);
  // Track if a fetch is in progress to prevent concurrent requests
  const fetchingRef = useRef(false);

  // Initialize Socket.io connection
  useEffect(() => {
    if (!socket) {
      socket = io(API_URL, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
      });

      socket.on('connect', () => {
        console.log('🔌 UWB WebSocket connected');
        setConnected(true);
      });

      socket.on('disconnect', () => {
        console.log('🔌 UWB WebSocket disconnected');
        setConnected(false);
      });

      socket.on('connect_error', (err) => {
        console.error('🔌 UWB WebSocket connection error:', err);
        setConnected(false);
      });
    }

    return () => {
      // Don't disconnect on unmount - keep connection alive for other components
    };
  }, []);

  // Subscribe to warehouse updates
  useEffect(() => {
    if (socket && warehouseId) {
      socket.emit('subscribe:warehouse', warehouseId);
      console.log(`📍 Subscribed to UWB updates for warehouse ${warehouseId}`);

      return () => {
        socket.emit('unsubscribe:warehouse', warehouseId);
        console.log(`📍 Unsubscribed from UWB updates for warehouse ${warehouseId}`);
      };
    }
  }, [warehouseId]);

  // Apply minimal visual smoothing (just for animation, backend already did the real filtering)
  const applyVisualSmoothing = (tagId, newPosition, confidence) => {
    // Reject very low confidence readings
    if (confidence < VISUAL_SMOOTHING.MIN_CONFIDENCE) {
      const lastPos = lastPositionsRef.current.get(tagId);
      return lastPos ? { x: lastPos.x, y: lastPos.y } : newPosition;
    }

    const lastPos = lastPositionsRef.current.get(tagId);
    
    // If no previous position, use new position directly
    if (!lastPos) {
      lastPositionsRef.current.set(tagId, { x: newPosition.x, y: newPosition.y, timestamp: Date.now() });
      return newPosition;
    }

    // Apply very light smoothing (70% new, 30% old) - just for visual smoothness
    const alpha = VISUAL_SMOOTHING.ALPHA;
    const smoothedX = lastPos.x * (1 - alpha) + newPosition.x * alpha;
    const smoothedY = lastPos.y * (1 - alpha) + newPosition.y * alpha;

    lastPositionsRef.current.set(tagId, { x: smoothedX, y: smoothedY, timestamp: Date.now() });
    return { x: smoothedX, y: smoothedY };
  };

  // Listen for real-time position updates
  useEffect(() => {
    if (!socket) return;

    const handlePositionUpdate = (data) => {
      // NO WAREHOUSE FILTERING - process all position updates regardless of warehouse

      // Ghost tags (emitted in 'all' mode) go into a separate state
      if (data.isGhost) {
        const displayPosition = applyVisualSmoothing(
          data.tagId,
          data.position,
          data.confidence || 0.5
        );
        setGhostTags(prev => {
          const idx = prev.findIndex(t => t.tag_id === data.tagId);
          const entry = {
            tag_id: data.tagId,
            tag_hardware_id: data.tagHardwareId,
            tag_name: data.tagName,
            real_tag_id: data.realTagId,
            positioning_method: data.positioningMethod, // 'nls' | 'pairwise' | 'scipy' | 'linear'
            position: displayPosition,
            confidence: data.confidence,
            hub_count: data.hubCount,
            position_timestamp: data.timestamp,
            warehouse_id: data.warehouseId,
            status: 'active',
            last_seen_at: data.timestamp
          };
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = entry;
            return updated;
          }
          return [...prev, entry];
        });
        return;
      }

      // Backend already did all the filtering and Kalman smoothing
      // Just apply minimal visual smoothing for animation smoothness
      const displayPosition = applyVisualSmoothing(
        data.tagId,
        data.position,
        data.confidence || 0.5
      );

      // Update position and metadata immediately
      setUwbTags(prevTags => {
        const allTags = prevTags;
        const existingIndex = allTags.findIndex(t => t.tag_id === data.tagId);
        
        if (existingIndex >= 0) {
          // Update existing tag
          const updated = [...allTags];
          updated[existingIndex] = {
            ...updated[existingIndex],
            position: displayPosition,
            confidence: data.confidence,
            hub_count: data.hubCount,
            position_timestamp: data.timestamp,
            last_seen_at: data.timestamp,
            positioning_method: data.positioningMethod
          };
          return updated;
        } else {
          // Add new tag
          return [...allTags, {
            tag_id: data.tagId,
            tag_hardware_id: data.tagHardwareId,
            tag_name: data.tagName,
            position: displayPosition,
            confidence: data.confidence,
            hub_count: data.hubCount,
            position_timestamp: data.timestamp,
            warehouse_id: data.warehouseId,
            status: 'active',
            last_seen_at: data.timestamp,
            positioning_method: data.positioningMethod
          }];
        }
      });
    };

    socket.on('uwb:position', handlePositionUpdate);

    return () => {
      socket.off('uwb:position', handlePositionUpdate);
    };
  }, [warehouseId]);

  // Fetch initial UWB tags
  const fetchUWBTags = useCallback(async () => {
    // Prevent concurrent fetches
    if (fetchingRef.current) {
      console.log('[UWB] Fetch already in progress, skipping...');
      return;
    }
    
    try {
      fetchingRef.current = true;
      setLoading(true);
      setError(null);

      const token = localStorage.getItem(TOKEN_KEY);
      const url = warehouseId 
        ? `${API_URL}/api/uwb/tags?warehouseId=${warehouseId}`
        : `${API_URL}/api/uwb/tags`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch UWB tags');
      }

      const data = await response.json();
      let tags = data.tags || [];
      
      console.log(`[UWB] Fetched ${tags.length} tags from API for warehouse ${warehouseId || 'all'}`);
      
      // Don't filter tags on initial fetch - let them all through
      // Filtering will happen at render time in WarehouseMap.js
      // This ensures tags with positions always show up, even if warehouse_id is wrong
      
      // Log tags without positions with full details (only once per tag to avoid spam)
      const tagsWithoutPosition = tags.filter(t => !t.position || typeof t.position?.x !== 'number');
      if (tagsWithoutPosition.length > 0) {
        // Only check readings if we haven't already fetched them and there are new tags to warn about
        const needsReadingsCheck = tagsWithoutPosition.some(t => {
          const tagKey = `${t.tag_id || t.tag_hardware_id}`;
          return !loggedWarningsRef.current.has(tagKey);
        });
        
        if (needsReadingsCheck && !readingsFetchedRef.current) {
          readingsFetchedRef.current = true;
          
          // Check for recent readings once for all tags (to avoid multiple API calls)
          (async () => {
            let allReadings = [];
            try {
              const token = localStorage.getItem(TOKEN_KEY);
              const readingsUrl = warehouseId 
                ? `${API_URL}/api/uwb/readings/recent?warehouseId=${warehouseId}`
                : `${API_URL}/api/uwb/readings/recent`;
              const readingsResponse = await fetch(readingsUrl, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              if (readingsResponse.ok) {
                const readingsData = await readingsResponse.json();
                if (readingsData.success && readingsData.readings) {
                  allReadings = readingsData.readings;
                }
              }
            } catch (err) {
              // Silently fail
            }
            
            // Log warnings for all tags without positions
            tagsWithoutPosition.forEach(t => {
              const tagKey = `${t.tag_id || t.tag_hardware_id}`;
              if (!loggedWarningsRef.current.has(tagKey)) {
                loggedWarningsRef.current.add(tagKey);
                
                // Filter readings for this specific tag
                const recentReadings = allReadings.filter(r => 
                  r.tagId === t.tag_id || r.tagHardwareId === t.tag_hardware_id
                );
                
                console.group(`[UWB] ⚠️ Tag "${t.tag_name || t.tag_hardware_id}" has no position`);
                console.log('Tag Details:', {
                  name: t.tag_name,
                  hardware_id: t.tag_hardware_id,
                  database_id: t.tag_id,
                  status: t.status,
                  warehouse_id: t.warehouse_id,
                  hub_count: t.hub_count || 0,
                  last_seen_at: t.last_seen_at,
                  position_timestamp: t.position_timestamp,
                  hasPosition: !!(t.position && typeof t.position.x === 'number'),
                  position: t.position
                });
                
                if (recentReadings.length > 0) {
                  console.warn(`📡 Recent Readings (last 15s): Found ${recentReadings.length} hub(s) sending data:`, recentReadings.map(r => ({
                    hub: r.hubName || r.hubUwbAnchorId,
                    distance: `${r.latestDistance?.toFixed(2)}m`,
                    lastReading: new Date(r.lastReadingAt).toLocaleTimeString()
                  })));
                  if (recentReadings.length < 3) {
                    console.warn(`⚠️ Only ${recentReadings.length} hub(s) detected - need at least 3 for position calculation`);
                  }
                } else {
                  console.error('❌ No recent readings found - tag is not receiving data from any hubs');
                }
                
                console.warn('📍 Position Calculation Requirements:');
                console.warn('  • Needs distance readings from at least 3 hubs simultaneously');
                console.warn('  • Hubs must have valid positions set in the warehouse');
                console.warn('  • Tag must be within UWB range (~100m) of at least 3 hubs');
                console.warn('  • Backend collects readings from last 3 seconds');
                console.warn('🔍 Quick Checks:');
                console.warn('  1. Go to Build Mode → Verify at least 3 hubs have positions');
                console.warn('  2. Check server console for "[UWB] Reading:" messages');
                console.warn('  3. Verify tag is physically near at least 3 hubs');
                console.warn('  4. Check if hubs are "active" and sending readings');
                console.groupEnd();
              }
            });
          })();
        } else if (!needsReadingsCheck) {
          // Just log simple warnings for tags we've already warned about (no API call)
          tagsWithoutPosition.forEach(t => {
            const tagKey = `${t.tag_id || t.tag_hardware_id}`;
            if (!loggedWarningsRef.current.has(tagKey)) {
              loggedWarningsRef.current.add(tagKey);
              console.warn(`[UWB] ⚠️ Tag "${t.tag_name || t.tag_hardware_id}" still has no position`);
            }
          });
        }
      }
      
      // Initialize last positions for fetched tags
      tags.forEach(tag => {
        if (tag.position && tag.tag_id) {
          lastPositionsRef.current.set(tag.tag_id, {
            x: tag.position.x,
            y: tag.position.y,
            timestamp: Date.now()
          });
        }
      });
      
      setUwbTags(tags);
    } catch (err) {
      console.error('Error fetching UWB tags:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [warehouseId]); // Removed 'hubs' - it's not used in this function and was causing infinite loops

  // Fetch on mount and when warehouse changes
  useEffect(() => {
    fetchUWBTags();
  }, [fetchUWBTags]);

  // Listen for method changes broadcast from server (so all tabs stay in sync)
  useEffect(() => {
    if (!socket) return;
    const handleMethodChanged = (data) => {
      if (String(data.warehouseId) === String(warehouseId)) {
        setPositioningMethodState(data.method);
      }
    };
    socket.on('uwb:method_changed', handleMethodChanged);
    return () => socket.off('uwb:method_changed', handleMethodChanged);
  }, [warehouseId]);

  /**
   * Switch the positioning algorithm for the current warehouse.
   * Emits a socket event so the backend switches immediately.
   * @param {'nls'|'pairwise'|'scipy'|'linear'|'all'} method
   */
  const setPositioningMethod = useCallback((method) => {
    if (!socket || !warehouseId) return;
    socket.emit('uwb:set_method', { warehouseId, method });
    setPositioningMethodState(method); // optimistic update
    // Clear ghost tags when leaving 'all' mode
    if (method !== 'all') {
      setGhostTags([]);
    }
  }, [warehouseId]);

  // Cleanup last positions when warehouse changes
  useEffect(() => {
    // Clear last positions when switching warehouses
    lastPositionsRef.current.clear();
    
    // Clear logged warnings when switching warehouses (so we can log again for new warehouse)
    loggedWarningsRef.current.clear();
    
    // Reset readings fetch flag when switching warehouses
    readingsFetchedRef.current = false;
    
    // Don't filter tags here - let them all stay and filter at render time
    // This ensures tags with positions always show up, even if warehouse_id is wrong
  }, [warehouseId, hubs]);

  // Create a new UWB tag
  const createTag = useCallback(async (tagData) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await fetch(`${API_URL}/api/uwb/tags`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tagData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create tag');
      }

      const data = await response.json();
      await fetchUWBTags(); // Refresh list
      return data.tag;
    } catch (err) {
      console.error('Error creating UWB tag:', err);
      throw err;
    }
  }, [fetchUWBTags]);

  // Update a UWB tag
  const updateTag = useCallback(async (tagId, updates) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await fetch(`${API_URL}/api/uwb/tags/${tagId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update tag');
      }

      const data = await response.json();
      await fetchUWBTags(); // Refresh list
      return data.tag;
    } catch (err) {
      console.error('Error updating UWB tag:', err);
      throw err;
    }
  }, [fetchUWBTags]);

  // Delete a UWB tag
  const deleteTag = useCallback(async (tagId) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await fetch(`${API_URL}/api/uwb/tags/${tagId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete tag');
      }

      await fetchUWBTags(); // Refresh list
    } catch (err) {
      console.error('Error deleting UWB tag:', err);
      throw err;
    }
  }, [fetchUWBTags]);

  // Get UWB tags for a specific asset
  const getTagsByAsset = useCallback(async (assetId) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await fetch(`${API_URL}/api/uwb/tags/by-asset/${assetId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tags for asset');
      }

      const data = await response.json();
      return data.tags || [];
    } catch (err) {
      console.error('Error fetching tags by asset:', err);
      throw err;
    }
  }, []);

  // Get historical trace for a tag
  const getTagTrace = useCallback(async (tagId, options = {}) => {
    try {
      const {
        startTime,
        endTime,
        limit = 1000,
        minConfidence = 0.3,
        minHubCount = 2,
        maxSpeed = 5.0,
        smoothing = 'kalman'
      } = options;

      const token = localStorage.getItem(TOKEN_KEY);
      const params = new URLSearchParams();
      
      if (startTime) params.append('startTime', startTime);
      if (endTime) params.append('endTime', endTime);
      params.append('limit', limit);
      params.append('minConfidence', minConfidence);
      params.append('minHubCount', minHubCount);
      params.append('maxSpeed', maxSpeed);
      params.append('smoothing', smoothing);

      const response = await fetch(`${API_URL}/api/uwb/tags/${tagId}/trace?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tag trace');
      }

      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Error fetching tag trace:', err);
      throw err;
    }
  }, []);

  return {
    uwbTags,
    ghostTags,
    loading,
    error,
    connected,
    positioningMethod,
    setPositioningMethod,
    refresh: fetchUWBTags,
    createTag,
    updateTag,
    deleteTag,
    getTagTrace,
    getTagsByAsset
  };
}
